/**
 * Utilities for exporting data to CSV format
 */

/**
 * Convert an array of objects to CSV string
 */
export function convertToCSV(data, headers) {
  if (!data || data.length === 0) return '';

  // If headers not provided, extract from first object
  if (!headers) {
    headers = Object.keys(data[0]);
  }

  // Build CSV header row
  const csvHeader = headers.map(header => `"${header}"`).join(',');

  // Build CSV data rows
  const csvRows = data.map(row => {
    return headers.map(header => {
      const value = row[header];

      // Handle null/undefined
      if (value === null || value === undefined) {
        return '';
      }

      // Handle arrays and objects - convert to JSON string
      if (typeof value === 'object') {
        return `"${JSON.stringify(value).replace(/"/g, '""')}"`;
      }

      // Escape quotes and wrap in quotes
      const stringValue = String(value).replace(/"/g, '""');
      return `"${stringValue}"`;
    }).join(',');
  });

  return [csvHeader, ...csvRows].join('\n');
}

/**
 * Flatten nested report data structure for CSV export
 */
export function flattenReportData(report, reportType) {
  switch (reportType) {
    case 'daily':
      return flattenDailyReport(report);
    case 'weekly':
      return flattenWeeklyReport(report);
    case 'monthly':
      return flattenMonthlyReport(report);
    case 'compliance':
      return flattenComplianceReport(report);
    case 'incident':
      return flattenIncidentReport(report);
    default:
      return flattenGenericReport(report);
  }
}

function flattenDailyReport(report) {
  const rows = [];

  if (report.summary) {
    rows.push({
      'Type': 'Summary',
      'Total Alerts': report.summary.totalAlerts,
      'Critical': report.summary.criticalAlerts,
      'High': report.summary.highAlerts,
      'Medium': report.summary.mediumAlerts,
      'Low': report.summary.lowAlerts,
      'PPE Violations': report.summary.ppeViolations,
      'Zone Breaches': report.summary.zoneBreaches,
      'Average Confidence': report.summary.avgConfidence?.toFixed(2) || 'N/A',
    });
  }

  if (report.byHour && Array.isArray(report.byHour)) {
    report.byHour.forEach(hour => {
      rows.push({
        'Type': 'Hourly',
        'Hour': hour.hour || 'N/A',
        'Alert Count': hour.count || 0,
        'Critical': hour.critical || 0,
        'High': hour.high || 0,
      });
    });
  }

  if (report.topAlertTypes && Array.isArray(report.topAlertTypes)) {
    report.topAlertTypes.forEach(alert => {
      rows.push({
        'Type': 'Top Alert Type',
        'Alert Type': alert.type || 'N/A',
        'Count': alert.count || 0,
        'Severity': alert.severity || 'N/A',
      });
    });
  }

  return rows.length > 0 ? rows : [{ 'Type': 'No Data', 'Status': 'No alerts found for this period' }];
}

function flattenWeeklyReport(report) {
  const rows = [];

  if (report.summary) {
    rows.push({
      'Type': 'Summary',
      'Total Alerts': report.summary.totalAlerts,
      'Average Daily': report.summary.avgDaily?.toFixed(1) || 'N/A',
      'Peak Day': report.summary.peakDay || 'N/A',
      'Trend': report.summary.trend || 'N/A',
    });
  }

  if (report.dailyBreakdown && Array.isArray(report.dailyBreakdown)) {
    report.dailyBreakdown.forEach(day => {
      rows.push({
        'Type': 'Daily',
        'Date': day.date ? new Date(day.date).toISOString().split('T')[0] : 'N/A',
        'Alert Count': day.count || 0,
        'Critical': day.critical || 0,
        'High': day.high || 0,
        'Medium': day.medium || 0,
        'Low': day.low || 0,
      });
    });
  }

  return rows.length > 0 ? rows : [{ 'Type': 'No Data', 'Status': 'No data found for this period' }];
}

function flattenMonthlyReport(report) {
  const rows = [];

  if (report.summary) {
    rows.push({
      'Type': 'Summary',
      'Total Alerts': report.summary.totalAlerts,
      'Critical': report.summary.criticalAlerts,
      'High': report.summary.highAlerts,
      'Compliance Rate': report.summary.complianceRate ? `${report.summary.complianceRate.toFixed(1)}%` : 'N/A',
      'Key Incidents': report.summary.keyIncidents || 0,
    });
  }

  if (report.weeklyTrend && Array.isArray(report.weeklyTrend)) {
    report.weeklyTrend.forEach((week, idx) => {
      rows.push({
        'Type': 'Weekly',
        'Week': `Week ${idx + 1}`,
        'Alert Count': week.count || 0,
        'Change %': week.changePercent?.toFixed(1) || '0',
      });
    });
  }

  if (report.topViolations && Array.isArray(report.topViolations)) {
    report.topViolations.forEach(violation => {
      rows.push({
        'Type': 'Top Violation',
        'Violation': violation.type || 'N/A',
        'Count': violation.count || 0,
        'Workers Affected': violation.workersAffected || 0,
      });
    });
  }

  return rows.length > 0 ? rows : [{ 'Type': 'No Data', 'Status': 'No data found for this period' }];
}

function flattenComplianceReport(report) {
  const rows = [];

  if (report.summary) {
    rows.push({
      'Type': 'Summary',
      'Overall Compliance': report.summary.overallCompliance ? `${report.summary.overallCompliance.toFixed(1)}%` : 'N/A',
      'Total Workers': report.summary.totalWorkers || 0,
      'Compliant Workers': report.summary.compliantWorkers || 0,
      'Non-Compliant': report.summary.nonCompliantWorkers || 0,
    });
  }

  if (report.byEquipment && Array.isArray(report.byEquipment)) {
    report.byEquipment.forEach(equipment => {
      rows.push({
        'Type': 'Equipment',
        'Equipment': equipment.name || 'N/A',
        'Compliance %': equipment.compliance ? `${equipment.compliance.toFixed(1)}%` : 'N/A',
        'Violations': equipment.violations || 0,
      });
    });
  }

  if (report.byZone && Array.isArray(report.byZone)) {
    report.byZone.forEach(zone => {
      rows.push({
        'Type': 'Zone',
        'Zone': zone.name || 'N/A',
        'Compliance %': zone.compliance ? `${zone.compliance.toFixed(1)}%` : 'N/A',
        'Total Workers': zone.workers || 0,
        'Violations': zone.violations || 0,
      });
    });
  }

  return rows.length > 0 ? rows : [{ 'Type': 'No Data', 'Status': 'No compliance data found' }];
}

function flattenIncidentReport(report) {
  const rows = [];

  if (report.summary) {
    rows.push({
      'Type': 'Summary',
      'Total Incidents': report.summary.totalIncidents || 0,
      'Critical': report.summary.critical || 0,
      'Serious': report.summary.serious || 0,
      'Moderate': report.summary.moderate || 0,
      'Minor': report.summary.minor || 0,
      'Closed': report.summary.closed || 0,
      'Open': report.summary.open || 0,
    });
  }

  if (report.byType && Array.isArray(report.byType)) {
    report.byType.forEach(type => {
      rows.push({
        'Type': 'Incident Type',
        'Category': type.type || 'N/A',
        'Count': type.count || 0,
      });
    });
  }

  if (report.bySeverity && Array.isArray(report.bySeverity)) {
    report.bySeverity.forEach(severity => {
      rows.push({
        'Type': 'Severity',
        'Severity Level': severity.severity || 'N/A',
        'Count': severity.count || 0,
      });
    });
  }

  return rows.length > 0 ? rows : [{ 'Type': 'No Data', 'Status': 'No incident data found' }];
}

function flattenGenericReport(report) {
  // For unknown report types, flatten one level
  const rows = [];

  for (const [key, value] of Object.entries(report)) {
    if (key === 'type' || key === 'generatedAt' || key === 'dateRange' || key === 'sites') {
      continue; // Skip metadata
    }

    if (Array.isArray(value)) {
      rows.push({
        'Category': key,
        'Count': value.length,
      });
    } else if (typeof value === 'object' && value !== null) {
      rows.push({
        'Category': key,
        'Value': JSON.stringify(value),
      });
    } else {
      rows.push({
        'Category': key,
        'Value': String(value),
      });
    }
  }

  return rows.length > 0 ? rows : [{ 'Category': 'No Data', 'Status': 'No data found' }];
}

/**
 * Download CSV file
 */
export function downloadCSV(csvContent, filename) {
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.setAttribute('href', url);
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  window.URL.revokeObjectURL(url);
  document.body.removeChild(link);
}
